import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { CustomIntervalType as CustomIntervalTypeEnum } from "@sinch/core/entities/serverEnums";
import { useFormMutationSubmit } from "@sinch/hooks/useFormMutationSubmit";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { useSnackbar } from "notistack";
import { isNotNilOrEmpty } from "ramda-adjunct";
import React, { FC, Suspense, useCallback, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type {
  CreateIntervalMutation,
  CreateIntervalMutationVariables,
  EditIntervalMutation,
  EditIntervalMutationVariables,
} from "./queries.types";
import { CreateIntervalArchivedField } from "@sinch/components/application/RateInterval/CreateInterval/CreateIntervalArchivedField";
import { CreateIntervalDescriptionField } from "@sinch/components/application/RateInterval/CreateInterval/CreateIntervalDescriptionField";
import { CreateIntervalNameField } from "@sinch/components/application/RateInterval/CreateInterval/CreateIntervalNameField";
import { CreateIntervalTypeField } from "@sinch/components/application/RateInterval/CreateInterval/CreateIntervalTypeField";
import { CzechHolidaysSettings } from "@sinch/components/application/RateInterval/CreateInterval/CzechHolidaysSettings";
import { DailyOvertimeSettings } from "@sinch/components/application/RateInterval/CreateInterval/DailyOvertimeSettings";
import {
  type CreateIntervalFormData,
  intervalFormSchema,
} from "@sinch/components/application/RateInterval/CreateInterval/formSchema";
import { TimeIntervalSettings } from "@sinch/components/application/RateInterval/CreateInterval/TimeIntervalSettings";
import { WeeklyOvertimeSettings } from "@sinch/components/application/RateInterval/CreateInterval/WeeklyOvertimeSettings";
import { WorkedDurationSettings } from "@sinch/components/application/RateInterval/CreateInterval/WorkedDurationSettings";
import { useRateInterval } from "@sinch/components/application/RateInterval/UseRateInterval";
import { CollapsibleListCard } from "@sinch/components/CollapsibleListCard/CollapsibleListCard";
import { FormContainer } from "@sinch/components/form/FormContainer/FormContainer";
import { LoadingWrapper } from "@sinch/components/LoadingWrapper/LoadingWrapper";

interface IntervalDialogProps<TIntervalId extends string | undefined = string> {
  open: boolean;
  onClose: () => void;
  onSave: TIntervalId extends string ? (id?: string) => void : () => void;
  intervalId?: TIntervalId;
}

export const IntervalFormDialog: FC<IntervalDialogProps> = ({ open, onClose, onSave, intervalId }) => (
  <Dialog onClose={onClose} open={open}>
    <Suspense fallback={<LoadingWrapper on />}>
      <IntervalForm intervalId={intervalId} onClose={onClose} onSave={onSave} />
    </Suspense>
  </Dialog>
);

const IntervalForm: FC<Omit<IntervalDialogProps, "open">> = ({ intervalId, onSave, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const {
    create,
    edit,
    rateInterval: data,
    loading,
  } = useRateInterval({ id: intervalId, queryOptions: { fetchPolicy: "network-only" } });

  const defaultData = useMemo<CreateIntervalFormData | undefined>(
    () =>
      data?.rateInterval
        ? {
            name: data?.rateInterval.name,
            archived: data?.rateInterval.archived,
            type: data?.rateInterval.type,
            description: data?.rateInterval.description ?? "",
            settings: data?.rateInterval.settings,
          }
        : undefined,
    [data]
  );

  const form = useMuiForm<CreateIntervalFormData>({
    schema: intervalFormSchema,
    formOptions: {
      defaultValues: defaultData,
    },
  });
  const submitCreate = useFormMutationSubmit<
    CreateIntervalMutation,
    CreateIntervalMutationVariables,
    CreateIntervalFormData
  >({
    mutate: create,
    form,
    variableMapper: (dataToSend) => ({ input: dataToSend }),
  });
  const submitEdit = useFormMutationSubmit<EditIntervalMutation, EditIntervalMutationVariables, CreateIntervalFormData>(
    {
      mutate: edit,
      form,
      variableMapper: (dataToSend) => ({ id: intervalId!, input: dataToSend }),
    }
  );

  const handleSubmit = useCallback(async () => {
    if (intervalId) {
      const response = await submitEdit();
      if (response?.editRateInterval.result) {
        onClose();
        onSave();
        enqueueSnackbar(t("RateBuilder.addRateIntervalSaveSuccess"), { variant: "success" });
      }
    } else {
      const response = await submitCreate();
      if (response?.createRateInterval?.result && response.createRateInterval.payload) {
        onSave(response.createRateInterval.payload);
        onClose();
        enqueueSnackbar(t("RateBuilder.addRateIntervalSaveSuccess"), { variant: "success" });
      }
    }
  }, [onClose, onSave, submitCreate, submitEdit]);

  const usedInRatelists = data?.rateInterval?.ratelists ?? [];
  return (
    <FormContainer form={form}>
      <DialogTitle>{t("RateUnit.createCustomInterval")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {intervalId && <CreateIntervalArchivedField />}
          <CreateIntervalNameField />
          <CreateIntervalDescriptionField />
          <CreateIntervalTypeField />
          <IntervalTypeVariants />
          {isNotNilOrEmpty(usedInRatelists) && (
            <CollapsibleListCard
              items={usedInRatelists.map((rl) => rl.name) ?? []}
              summaryText={t("RateUnit.useInRatelistsCount", { count: usedInRatelists?.length ?? 0 })}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} type="button">
          {t("cancel")}
        </Button>
        <Button loading={loading} onClick={handleSubmit} type="button" variant="contained">
          {t("Action.save")}
        </Button>
      </DialogActions>
    </FormContainer>
  );
};

const IntervalTypeVariants: FC = () => {
  const type = useWatch<CreateIntervalFormData, "type">({ name: "type" });

  const getContent = () => {
    switch (type) {
      case CustomIntervalTypeEnum.Time:
        return <TimeIntervalSettings />;
      case CustomIntervalTypeEnum.CzHolidays:
        return <CzechHolidaysSettings />;
      case CustomIntervalTypeEnum.DailyOvertime:
        return <DailyOvertimeSettings />;
      case CustomIntervalTypeEnum.WeeklyOvertime:
        return <WeeklyOvertimeSettings />;
      case CustomIntervalTypeEnum.WorkedDuration:
        return <WorkedDurationSettings />;
      default:
        return null;
    }
  };

  return <>{getContent()}</>;
};
